import type { QueryReturnValue } from '@reduxjs/toolkit/query/react';

import type { AppError } from '../../utils/firebaseErrors';
import type { ReportDetail } from './report';
import { buildReportDetailStaleReadKeys } from '../staleReadFallback/queryKeys';
import {
  clearSuccessfulRead,
  recordSuccessfulRead,
  resolveStaleReadFallback,
  type StaleReadMeta,
} from '../staleReadFallback/staleReadCache';
import { isAuthoritativeReportAbsenceError } from './reportAbsenceError';

type ReportDetailReadResponse = ReportDetail | undefined;

const addStaleReadMeta = <T>(data: T, staleRead: StaleReadMeta): T => {
  if (Array.isArray(data)) {
    return Object.assign([...data], { staleRead }) as T;
  }

  if (data && typeof data === 'object') {
    return Object.assign({}, data, { staleRead }) as T;
  }

  return data;
};

export const readReportDetailFamily = async ({
  reportId,
  read,
  isPresenceLost,
  isInvalidQueryFailure,
  toInvalidQueryError,
  toAppError,
}: {
  reportId: string;
  read: () => Promise<ReportDetailReadResponse>;
  isPresenceLost?: (reportId: string) => boolean;
  isInvalidQueryFailure?: (error: unknown) => boolean;
  toInvalidQueryError?: (error: unknown) => AppError;
  toAppError?: (error: unknown) => AppError;
}): Promise<QueryReturnValue<ReportDetailReadResponse, AppError, {}>> => {
  const staleReadKeys = buildReportDetailStaleReadKeys(reportId);

  try {
    const reportDetail = await read();

    if (isPresenceLost?.(reportId)) {
      clearSuccessfulRead(staleReadKeys.exactKey, staleReadKeys.familyKey);
      return { data: undefined };
    }

    if (reportDetail) {
      recordSuccessfulRead(staleReadKeys.exactKey, staleReadKeys.familyKey, reportDetail);
    }

    return { data: reportDetail };
  } catch (error: unknown) {
    if (isInvalidQueryFailure?.(error)) {
      return {
        error: toInvalidQueryError ? toInvalidQueryError(error) : (error as AppError),
      };
    }

    if (isAuthoritativeReportAbsenceError(error)) {
      clearSuccessfulRead(staleReadKeys.exactKey, staleReadKeys.familyKey);
      const normalizedError = toAppError ? toAppError(error) : (error as AppError);

      return {
        error: {
          ...normalizedError,
          reason: 'report-absent',
        },
      };
    }

    const staleResult = resolveStaleReadFallback<ReportDetailReadResponse>(
      staleReadKeys.exactKey,
      staleReadKeys.familyKey,
      true
    );

    if (staleResult) {
      return { data: addStaleReadMeta(staleResult.data, staleResult.staleRead) };
    }

    return {
      error: toAppError ? toAppError(error) : (error as AppError),
    };
  }
};

export const clearReportDetailReadFamilyCache = (reportId: string): void => {
  const staleReadKeys = buildReportDetailStaleReadKeys(reportId);
  clearSuccessfulRead(staleReadKeys.exactKey, staleReadKeys.familyKey);
};
